import React, { useState, useEffect, useContext } from 'react';
import Login from './containers/loginPage/Login';
import Landing from './containers/landingPage/Landing';
import Sidebar from './components/sidebar/Sidebar';
import { useAuth0 } from '@auth0/auth0-react';
import AuthContext from './contexts/AuthContext';
import AuthService from './services/AuthService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GraphProvider } from './contexts/GraphContext';
import { Route, Routes, useNavigate, Navigate } from 'react-router';
import { Home } from './containers/homePage/Home';
import { Dashboard } from './containers/dashboardPage/Dashboard';
import { Explore } from './containers/explorePage/Explore';
import { Recent } from './containers/recentPage/Recent';
import { FollowedPath } from './containers/followedPathPage/FollowedPath';
import { FollowedTopic } from './containers/followedTopicPage/FollowedTopic';
import { Settings } from './containers/settingPage/Settings';
import { Help } from './containers/helpPage/Help';
import { About } from './containers/aboutPage/About';
import { AboutGraphRule } from './containers/aboutPage/AboutGraphRule';
import { Terms } from './containers/termsPage/Terms';
import { ForgotPassword } from './containers/forgotPasswordPage/ForgotPassword';
import { ResetPassword } from './containers/resetPasswordPage/ResetPassword';
import { Feedback } from './containers/feedbackPage/Feedback';
import { EditorPortal } from './containers/editorPortalPage/EditorPortal';
import { Topic } from './containers/topicPage/Topic';
import { Exercise } from './containers/exercisePage/Exercise';
import { SearchResult } from './containers/searchResultPage/SearchResult';
import NotFoundPage from './containers/notFoundPage/NotFoundPage';
import "react-loading-skeleton/dist/skeleton.css";
import { useLocation } from 'react-router-dom';

function App() {
    const navigate = useNavigate(); 
    const [auth, setAuth] = useState({ user: null, setUser: setUser });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loadingUser, setLoadingUser] = useState(true); // Add loading state
    const location = useLocation();

    function setUser(user) {
        setAuth((prevState) => ({ ...prevState, user: user }));
    }

    useEffect(() => {
        const initializeAuth = async () => {
            const userSession = AuthService.getUserSession();
            if (userSession) {
                setUser(userSession);
            }
            setLoadingUser(false); // Set loading to false once auth is initialized
        };

        initializeAuth();
    }, []);

    // List of routes where the sidebar should not be shown
    const noSidebarRoutes = ['/', '/login', '/forgotpassword', '/resetpassword'];
    const allRoutes = ['/', '/login', '/home', '/search', '/about', '/graphrule', '/dashboard', '/recent', '/path', '/settings', '/feedback', '/topic', '/followedTopic', '/terms', '/forgotpassword', '/resetpassword'];

    // Determine whether to show the Sidebar based on the current route
    const isNotFoundPage = !allRoutes.some(route =>
        location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    const shouldShowSidebar = !noSidebarRoutes.includes(location.pathname) && !isNotFoundPage;

    // Determine the appropriate class for the app container
    let containerClass = 'app-container';
    if (shouldShowSidebar) {
        if (location.pathname === '/home') {
            containerClass = 'app-container sidebar-no-margin';
        } else if (sidebarOpen) {
            containerClass = 'app-container sidebar-open';
        }
    } else {
        containerClass = 'app-container no-sidebar';
    }

    if (loadingUser) {
        return <div></div>; // Render a placeholder while loading
    }

    return (
        <AuthContext.Provider value={auth}>
            <GraphProvider>
                {shouldShowSidebar && <Sidebar onToggle={setSidebarOpen} />}
                <div className={containerClass}>
                    <ToastContainer />
                    <Routes>
                        {/* Routes accessible by all users (including guest) */}
                        <Route path='/home' element={<Home />} />
                        <Route path='/search' element={<SearchResult />} />
                        <Route path='/about' element={<About />} />
                        <Route path='/graphrule' element={<AboutGraphRule />} />
                        <Route path='/terms' element={<Terms />} />
                        <Route path='/forgotpassword' element={<ForgotPassword />} />
                        <Route path='/resetpassword' element={<ResetPassword />} />

                        {/* Redirect logged-in users away from login and landing pages */}
                        <Route path="/" element={auth.user ? <Navigate to="/home" replace /> : <Landing />} />
                        <Route path="/login" element={auth.user ? <Navigate to="/home" replace /> : <Login />} />

                        Routes accessible only by authenticated users 
                        {auth.user && (
                            <>
                                <Route path='/dashboard/:userProfileId' element={<Dashboard />} />
                                <Route path='/recent' element={<Recent />} />
                                <Route path='/path' element={<FollowedPath />} />
                                <Route path='/followedTopic' element={<FollowedTopic />} />
                                <Route path='/settings/:setting' element={<Settings />} />
                                <Route path='/settings' element={<Settings />} />
                                <Route path='/feedback' element={<Feedback />} />
                                <Route path='/topic/:id' element={<Topic />} />

                            </>
                        )}

                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </div>
            </GraphProvider>
        </AuthContext.Provider>
    );
}

export default App;
